"use client";

import React from "react";
import { Wrench, RefreshCw, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, Clock, DollarSign, ShieldAlert, PhoneCall, ArrowRight, Droplets, Flame } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const repairSigns = [{
  title: "Unit is Under 8-10 Years Old",
  desc: "Most tank water heaters last 10 to 12 years. If yours is newer, fixing components is usually the most cost-effective choice."
}, {
  title: "Faulty Heating Element or Thermostat",
  desc: "Lukewarm water or rapid heat loss often comes from a tripped breaker or a worn heating element that can be swapped out quickly."
}, {
  title: "Minor Valve or Connection Leaks",
  desc: "Drips from the temperature and pressure relief valve or external pipe fittings can be resealed or replaced without a new tank."
}, {
  title: "Pilot Light or Igniter Issues",
  desc: "Gas heaters that will not light or stay lit usually only require thermocouple cleaning or electronic igniter replacement."
}];
const replaceSigns = [{
  title: "Tank is Over 10-12 Years Old",
  desc: "Aging tanks face severe internal corrosion and mineral accumulation, making repairs short-lived and inefficient."
}, {
  title: "Rust or Corrosion on the Tank Body",
  desc: "Rust around the base or leaking tank seams indicates internal metal failure. This cannot be patched safely."
}, {
  title: "Loud Rumbling, Popping, or Banging",
  desc: "Thick hardened sediment at the bottom overheats the tank floor, traps steam pockets, and cracks the internal glass lining."
}, {
  title: "Frequent, Compounding Breakdowns",
  desc: "If repairs cost more than half the price of a brand-new energy-efficient unit, total replacement is the smarter financial move."
}];
const decisionRules = [{
  label: "The 50% Rule",
  text: "If a repair estimate equals 50% or more of the cost of a new water heater, replacement is the recommended path.",
  icon: DollarSign
}, {
  label: "System Age",
  text: "Gas units typically last 8 to 12 years; electric units last 10 to 15 years; tankless units can reach 20+ years with routine flushes.",
  icon: Clock
}, {
  label: "Safety & Water Damage",
  text: "A leaking tank can release 40 to 80 gallons of water into your home. Visible tank corrosion demands swift replacement.",
  icon: ShieldAlert
}];
export default function HeaterDecision() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="waterheaterrepairvsreplacement" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Clear Guidance
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Water Heater Repair vs. Replacement
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Wondering whether your hot water system can be saved or if it is time to install a new one?
            Here is a practical breakdown to help you make an informed, budget-friendly decision.
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Repair Column */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }}>
            <Card className="h-full border border-border bg-card shadow-md flex flex-col justify-between">
              <div>
                <CardHeader className="pb-4 border-b border-border">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                        <Wrench className="size-6" />
                      </div>
                      <CardTitle className="text-2xl font-bold font-default text-card-foreground">
                        When to Repair
                      </CardTitle>
                    </div>
                    <Badge variant="secondary" className="font-semibold text-xs">
                      Cost-Effective Fix
                    </Badge>
                  </div>
                  <CardDescription className="text-muted-foreground font-default">
                    Ideal for younger systems experiencing isolated mechanical or electrical component issues.
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-6 space-y-4">
                  {repairSigns.map((sign, i) => <motion.div key={sign.title} variants={itemVariants} data-index={i} className="flex items-start gap-3.5 p-3.5 rounded-lg bg-background border border-border/60">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm sm:text-base text-foreground font-default">
                          {sign.title}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground font-default mt-0.5">
                          {sign.desc}
                        </p>
                      </div>
                    </motion.div>)}
                </CardContent>
              </div>

              <CardFooter className="pt-4 pb-6 flex flex-col sm:flex-row gap-3">
                <Button asChild variant="default" className="w-full">
                  <Link to="/contact/">
                    Schedule Repair Service
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/water-heater/">Learn About Water Heaters</Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Replacement Column */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }}>
            <Card className="h-full border border-border bg-card shadow-md flex flex-col justify-between">
              <div>
                <CardHeader className="pb-4 border-b border-border">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-accent/20 text-accent-foreground">
                        <RefreshCw className="size-6" />
                      </div>
                      <CardTitle className="text-2xl font-bold font-default text-card-foreground">
                        When to Replace
                      </CardTitle>
                    </div>
                    <Badge variant="destructive" className="font-semibold text-xs">
                      Long-Term Investment
                    </Badge>
                  </div>
                  <CardDescription className="text-muted-foreground font-default">
                    Recommended when the tank integrity is compromised or the unit has reached the end of its lifespan.
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-6 space-y-4">
                  {replaceSigns.map((sign, i) => <motion.div key={sign.title} variants={itemVariants} data-index={i} className="flex items-start gap-3.5 p-3.5 rounded-lg bg-background border border-border/60">
                      <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm sm:text-base text-foreground font-default">
                          {sign.title}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground font-default mt-0.5">
                          {sign.desc}
                        </p>
                      </div>
                    </motion.div>)}
                </CardContent>
              </div>

              <CardFooter className="pt-4 pb-6 flex flex-col sm:flex-row gap-3">
                <Button asChild variant="default" className="w-full">
                  <Link to="/contact/">
                    Get Replacement Quote
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/brands/">View Supported Brands</Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </div>

        {/* Rule of Thumb / Decision Factors */}
        <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-secondary border border-border">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-border">
            <div>
              <div className="flex items-center gap-2 text-primary mb-1">
                <Flame className="size-5" />
                <span className="text-xs font-bold uppercase tracking-wider">Professional Advice</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-secondary-foreground font-default">
                Fast Guidelines For Jonesboro Homeowners
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <Droplets className="size-5 text-primary" />
              <span className="text-sm font-medium text-secondary-foreground">
                24/7 Rapid Diagnostics Available
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
            {decisionRules.map((rule, i) => {
            const IconComp = rule.icon;
            return <div key={rule.label} data-index={i} className="flex flex-col gap-2 p-4 rounded-xl bg-background border border-border shadow-xs">
                  <div className="flex items-center gap-2 text-primary">
                    <IconComp className="size-5" />
                    <span className="font-bold text-foreground font-default text-base">{rule.label}</span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground font-default leading-relaxed">
                    {rule.text}
                  </p>
                </div>;
          })}
          </div>
        </div>

        {/* Emergency Help Banner */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 sm:p-6 rounded-xl bg-card border border-border shadow-sm">
          <div className="text-center sm:text-left">
            <h4 className="text-base sm:text-lg font-bold text-foreground font-default">
              Still unsure what your water heater needs?
            </h4>
            <p className="text-xs sm:text-sm text-muted-foreground font-default">
              Our licensed technicians perform on-site testing and provide honest, upfront recommendations.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild variant="outline" size="sm">
              <Link to="/problems/">Common Water Problems</Link>
            </Button>
            <Button asChild variant="default" size="sm">
              <a href="tel:4708479076" className="inline-flex items-center gap-1.5">
                <PhoneCall className="size-4" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}